import { useNowMinute } from "~/hooks/useNowMinute";
import { useProviderUsageRead } from "~/usage/useProviderUsage";
import {
  isSubscriptionProvider,
  resolveComposerUsageAccount,
  selectComposerPlanUsage,
} from "../composerPlanUsage";
import { buildComposerUsageNoticeItem } from "./composerUsageNoticeItem";
import {
  composerUsageNoticeContextIdentity,
  type ComposerUsageNoticeContext,
} from "./composerUsageNoticeContext";
import { useComposerUsageNoticeState } from "./useComposerUsageNoticeState";

export function useComposerUsageNotice(context: ComposerUsageNoticeContext): {
  readonly item: ReturnType<typeof buildComposerUsageNoticeItem> | null;
  readonly open: () => boolean;
  readonly close: () => void;
  readonly clearForAcceptedTurn: (threadId: string) => void;
} {
  const usage = useProviderUsageRead({
    enabled: Boolean(
      context.modelSelection && isSubscriptionProvider(context.modelSelection.provider),
    ),
  });
  const identity = composerUsageNoticeContextIdentity(
    context,
    usage.accounts,
    usage.accountsResolved,
  );
  const noticeState = useComposerUsageNoticeState(identity, context.threadId);
  const nowMinute = useNowMinute();
  const planUsage = context.modelSelection
    ? selectComposerPlanUsage({
        modelSelection: context.modelSelection,
        rateLimitStatus: context.rateLimitStatus,
        usageAccounts: usage.accounts,
        nowMs: Date.parse(`${nowMinute}:00.000Z`),
      })
    : null;
  const credits = context.modelSelection
    ? (resolveComposerUsageAccount({
        modelSelection: context.modelSelection,
        usageAccounts: usage.accounts,
      })?.accountView?.snapshot?.credits ?? null)
    : null;

  return {
    ...noticeState,
    item:
      noticeState.isOpen && identity
        ? buildComposerUsageNoticeItem({
            key: identity.key,
            planUsage,
            credits,
            isPending: usage.isPending,
            isError: usage.isError,
            onDismiss: noticeState.close,
          })
        : null,
  };
}
